import { Trans, useTranslation } from 'react-i18next';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ClbLoading } from '@celebration/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StatusCodes } from 'http-status-codes';
import Subheader from '../../common/components/subheader';
import PoolConstants from './constants';
import HomeConstants from '../home/constants';
import RoundmanagerApi from '../../common/services/roundmanager-api';
import PoolLoadCard from './components/pool-load-card';
import ILoadListData from '../../common/interfaces/loads/ILoadListData';
import { getPoolLoadCardInfo } from './components/pool-load-card/utils/pool-load-card-utils';
import PoolInfo from './components/pool-info';
import LoadsUtils from '../business/loads/utils';
import IFiltersModel from '../../common/interfaces/IFiltersModel';
import LoadsFilters from '../../common/components/loads-filters';
import './pool.scss';
import { useToast } from '../../app/contexts/toast/toast.context';
import ConfirmationModal from '../../common/components/confirmation-modal';
import ConfirmationTermConstants from '../smartcargas/confirmation-term/constants';
import ErrorModal from '../../common/components/error-modal';
import { useConnection } from '../../app/contexts/connection/connection-context';
import ConnectionError from '../../common/components/connection-error/connection-error';
import SelectAutocomplete from '../../common/components/select-autocomplete';
import ILoadRateByVehicle from '../../common/interfaces/ILoadRateByVehicle';
import { formatCurrency } from '../../utils/currency-utils';

interface ILoadToConfirm {
    loadId: string;
    currency: string;
    ratesByVehicle: ILoadRateByVehicle[];
    selectedRate: ILoadRateByVehicle | null;
}

export default function PoolPage() {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { connection, isConnected } = useConnection();

    const page = useRef(0);
    const hasMore = useRef(true);
    const noRates = useRef(0);
    const totalLoads = useRef(0);
    const numberOfSelectedCards = useRef(0);
    const filters = useRef<IFiltersModel>({} as IFiltersModel);

    const [hasError, setHasError] = useState(false);
    const [loadList, setLoadList] = useState<ILoadListData[]>([]);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [loadToConfirm, setLoadToConfirm] = useState<ILoadToConfirm | null>(
        null,
    );
    const [isConfirming, setIsConfirming] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);

    const loadListEndMessage = useMemo(() => {
        if (hasError) {
            return (
                <h2 data-testid="loads__fetch-loads-error-message">
                    {t('CargasErroConsultar')}
                </h2>
            );
        }
        if (loadList.length > 0) {
            return <h2>{t('CargasTodasJaExibidas')}</h2>;
        }

        return <h2>{t('CargasExibirNenhuma')}</h2>;
    }, [loadList.length, hasError, t]);

    const breadcrumbList: BreadcrumbItem[] = [
        { label: HomeConstants.PAGE_TITLE, url: HomeConstants.PATH },
        { label: t(PoolConstants.PAGE_TITLE), url: PoolConstants.PATH },
    ];

    const fetchData = useCallback(async () => {
        const { response, error } = await RoundmanagerApi.Pool.getPoolLoads(
            page.current,
            30,
            filters.current,
        );

        if (error !== undefined) {
            hasMore.current = false;
            setHasError(true);
            return;
        }

        hasMore.current = page.current < response!.totalPages;
        noRates.current = response!.loadsWithoutRateCount;
        totalLoads.current = response!.totalItems;

        setLoadList(
            response!.currentPage === 1
                ? response!.data
                : current => current.concat(response!.data),
        );
    }, []);

    const fetchNextPage = useCallback(async () => {
        page.current++;

        if (page.current === 1 || hasMore.current) {
            await fetchData();
        }
    }, [fetchData]);

    const fetchFirstPage = useCallback(async () => {
        page.current = 1;
        hasMore.current = true;
        setLoadList([]);
        fetchData();
    }, [fetchData]);

    const onApplyFilters = useCallback(
        async (filtersModel: IFiltersModel) => {
            filters.current = filtersModel;
            await fetchFirstPage();
        },
        [fetchFirstPage],
    );

    const handleNotification = useCallback(
        (notification: {
            type: string;
            country: string;
            loadId: string;
            status: string;
            carrierCode: string;
        }) => {
            const idx = loadList.findIndex(
                l => l.loadId === notification.loadId,
            );

            if (idx !== -1) {
                loadList[idx].confirmedBy = notification.carrierCode;
                loadList[idx].status = notification.status;
                setLoadList([...loadList]);
            }
        },
        [loadList],
    );

    const openConfirmationModal = useCallback((load: ILoadListData) => {
        setLoadToConfirm({
            loadId: load.loadId,
            currency: load.shipment.currency,
            ratesByVehicle: load.ratesByVehicle,
            selectedRate: load.ratesByVehicle[0] ?? null,
        });
        setConfirmationModalOpen(true);
    }, []);

    const closeConfirmationModal = useCallback(() => {
        setConfirmationModalOpen(false);
        setLoadToConfirm(null);
        setIsConfirming(false);
    }, []);

    const handleConfirmLoad = useCallback(async () => {
        if (!loadToConfirm?.selectedRate) return;

        setIsConfirming(true);

        try {
            const { error, statusCode } =
                await RoundmanagerApi.Pool.confirmPoolLoads(
                    [loadToConfirm.loadId],
                    loadToConfirm.selectedRate.vehicle.name,
                );

            if (statusCode === StatusCodes.CONFLICT) {
                setErrorModalOpen(true);
            } else if (error) {
                setToast(
                    'negative',
                    t('Erro'),
                    error.message || t('ErroConfirmarCarga'),
                );
            } else {
                setToast('positive', t('Sucesso'), t('CargaConfirmadaSucesso'));

                await fetchFirstPage();
            }
        } catch (err: any) {
            setToast(
                'negative',
                t('Erro'),
                err.message || t('ErroConfirmarCarga'),
            );
        } finally {
            closeConfirmationModal();
        }
    }, [loadToConfirm, setToast, t, fetchFirstPage, closeConfirmationModal]);

    const confirmLoad = async (load: ILoadListData) => {
        openConfirmationModal(load);
    };

    const handleSelectedVehicleChange = useCallback((vehicleName: string) => {
        setLoadToConfirm(current => {
            const selectedRate = current?.ratesByVehicle.find(
                rate => rate.vehicle.name === vehicleName,
            );

            return current && selectedRate
                ? { ...current, selectedRate }
                : current;
        });
    }, []);

    useEffect(() => {
        if (page.current === 0) {
            fetchFirstPage();
        }
    }, [fetchFirstPage]);

    useEffect(() => {
        if (isConnected) {
            connection?.on('Notification', handleNotification);
        }

        return () => {
            if (isConnected) {
                connection?.off('Notification');
            }
        };
    }, [isConnected, connection, handleNotification]);

    return (
        <div className="pool-grid-page">
            <Subheader
                title={t(PoolConstants.PAGE_SUBTITLE)}
                breadcrumbList={breadcrumbList}
                dataTestid="pool-subheader"
            >
                <PoolInfo
                    totalLoads={totalLoads.current}
                    noRates={noRates.current}
                />
            </Subheader>

            <LoadsFilters
                filters={filters}
                selectedCards={loadList
                    .filter(x => x.checked)
                    .map(x => LoadsUtils.fillLoadInfo(x))}
                onApplyFilters={onApplyFilters}
                onSelectAllCards={() => {}}
                onCleanCardSelection={() => {}}
                fetchFirstPage={fetchFirstPage}
                isPool
            />

            <div
                id="pool-content"
                className="pool-content"
                data-testid="pool-content-scrollable"
            >
                <InfiniteScroll
                    className="pool-list"
                    dataLength={loadList.length}
                    next={fetchNextPage}
                    hasMore={hasMore.current}
                    loader={<ClbLoading />}
                    endMessage={loadListEndMessage}
                    scrollableTarget="pool-content"
                >
                    {loadList.length > 0 &&
                        loadList.map((load: ILoadListData) => {
                            return (
                                <PoolLoadCard
                                    key={`${load.loadId}`}
                                    loadInfo={getPoolLoadCardInfo(load)}
                                    numberOfSelectedCards={
                                        numberOfSelectedCards.current
                                    }
                                    onConfirm={() => confirmLoad(load)}
                                />
                            );
                        })}
                </InfiniteScroll>
            </div>

            <ConfirmationModal
                isOpen={confirmationModalOpen}
                title={t(
                    loadToConfirm?.selectedRate
                        ? 'ConfirmationModalTitle'
                        : 'PoolConfirmationUnavailableTitle',
                )}
                description={
                    loadToConfirm?.selectedRate ? (
                        <Trans
                            i18nKey="ConfirmationModalDescription"
                            values={{
                                loadId: loadToConfirm.loadId,
                                vehicleDescription:
                                    loadToConfirm.selectedRate.vehicle
                                        .description,
                            }}
                            components={{ bold: <strong /> }}
                        />
                    ) : null
                }
                actionLink={
                    loadToConfirm?.selectedRate ? (
                        <a
                            href={`/${ConfirmationTermConstants.default}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="confirmation-modal-contract-link"
                        >
                            {t('ConfirmationModalContractLink')}
                        </a>
                    ) : undefined
                }
                onConfirm={handleConfirmLoad}
                onCancel={closeConfirmationModal}
                cancelButtonText={
                    loadToConfirm?.selectedRate ? undefined : t('Fechar')
                }
                isLoading={isConfirming}
                confirmDisabled={!loadToConfirm?.selectedRate}
            >
                {loadToConfirm && loadToConfirm.ratesByVehicle.length === 0 ? (
                    <output>{t('PoolNoEligibleVehicleRate')}</output>
                ) : (
                    <SelectAutocomplete
                        label={t('CaminhaoEscolhido')}
                        placeholder={t('CaminhaoEscolhido')}
                        value={loadToConfirm?.selectedRate?.vehicle.name}
                        onChange={handleSelectedVehicleChange}
                        options={
                            loadToConfirm
                                ? loadToConfirm.ratesByVehicle.map(rate => ({
                                      label: `${rate.vehicle.description} - ${formatCurrency(
                                          rate.chargeAmount,
                                          loadToConfirm.currency,
                                      )}`,
                                      value: rate.vehicle.name,
                                  }))
                                : []
                        }
                        dataTestId="pool-confirmation-vehicle-choice"
                        getPopupContainer={node =>
                            node.parentNode as HTMLElement
                        }
                    />
                )}
            </ConfirmationModal>

            <ErrorModal
                isOpen={errorModalOpen}
                title={t('ErrorModalTitle')}
                description={t('ErrorModalDescription')}
                onClose={() => setErrorModalOpen(false)}
            />

            <ConnectionError />
        </div>
    );
}
